import { DUPE_API_URL, MAX_REDIRECT_QUERY_LENGTH } from '@dupe/common/constants'
import { EXPERIMENT_ARM_PARAM } from '@dupe/common/experimentArms'
import type { ChatResultPathway } from '@dupe/common/constants'
import type { ImpressionSource, ResultLeg } from '@dupe/common/types'
import {
  normalizeProductImageUrl,
  stripDoubledProtocol,
} from '@dupe/common/helpers'
import axios from 'axios'
import type { ParsedUrlQuery } from 'querystring'
import { getDomain, parse as parseDomain } from 'tldts'
import type { ResultsResp } from '~/src/db/queryResultsResp'
import { getExtensionStateSnapshot } from '~/src/lib/extensionStateSnapshot'
import {
  moveChatContextOffRedirectUrl,
  redirectPageSourceUrl,
  withRestoredChatContext,
} from '~/src/lib/redirectChatContext'
import { getSignupWallVariantSnapshot } from '~/src/lib/signupWallVariantSnapshot'
import { getExperimentArmsParam } from '~/src/lib/experimentArmSnapshot'
import type { StackDrawerPlacement } from '~/src/context/StackDrawerContext'

export const createPublicUrl = (
  path: string,
  {
    excludeProtocol,
    publicUrlOverride,
    local,
  }: {
    excludeProtocol?: boolean
    publicUrlOverride?: string
    local?: boolean
  } = {}
): string => {
  const vercelUrl = process.env.NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL
    ? `https://${process.env.NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL}`
    : undefined

  const url = `${(
    local
      ? ''
      : publicUrlOverride ||
        process.env.NEXT_PUBLIC_PUBLIC_URL ||
        vercelUrl ||
        'https://dupe.com'
  ).replace(/\/$/, '')}/${path.replace(/^\//, '')}`

  // exclude protocol if requested
  if (url && excludeProtocol) {
    return url.replace(/^https?:\/\//, '')
  }

  return url
}

export const getCurrencySymbolFromPrice = (
  price: string
): 'USD' | 'GBP' | 'EUR' => {
  // look for symbol in price e.g $ or £ or €
  const symbol = price.match(/[£$€]/)?.[0]
  if (symbol === '£') return 'GBP'
  if (symbol === '$') return 'USD'
  if (symbol === '€') return 'EUR'

  return 'USD'
}

export const currencyFormatter = (
  amount: number,
  {
    currency = 'USD',
    currencySymbol = '$',
    round,
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
    locales = 'en-US',
  }: {
    currency?: string
    currencySymbol?: string
    round?: boolean
    minimumFractionDigits?: number
    maximumFractionDigits?: number
    locales?: string | string[]
  } = {}
) => {
  if (!amount) return ''

  const cleanAmount = parseFloat(`${amount}`.replace(/^\D+/g, ''))
  // if isNan revert back to preious value
  amount = isNaN(cleanAmount) ? amount : cleanAmount

  let currencyBasedOnCountry = currency

  switch (currencySymbol) {
    case '£':
    case 'GBP':
      currencyBasedOnCountry = 'GBP'
      break
    case '€':
    case 'EUR':
      currencyBasedOnCountry = 'EUR'
      break
    case '¥':
    case 'JPY':
      currencyBasedOnCountry = 'JPY'
      break
    case 'A':
    case 'A$':
    case 'AUD':
      currencyBasedOnCountry = 'AUD'
      break
    case '$':
    case 'USD':
    case 'US$':
      currencyBasedOnCountry = 'USD'
      break
    default:
      currencyBasedOnCountry = 'USD'
      break
  }

  const shouldUseParam =
    currency !== 'USD' && currencySymbol === '$' ? true : false

  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: shouldUseParam
      ? currency || currencyBasedOnCountry
      : currencyBasedOnCountry,
    minimumFractionDigits,
    maximumFractionDigits,
  }

  try {
    return new Intl.NumberFormat(locales, options).format(
      round ? Math.round(amount) : amount
    )
  } catch (_e) {
    return new Intl.NumberFormat(locales, {
      ...options,
      currency: 'USD',
    }).format(round ? Math.round(amount) : amount)
  }
}

export const pctFormatter = (
  amount: number,
  {
    locales = 'en-US',
    minimumFractionDigits = 0,
  }: {
    locales?: string | string[]
    minimumFractionDigits?: number
  } = {}
) =>
  amount !== undefined
    ? new Intl.NumberFormat(locales, {
        style: 'percent',
        minimumFractionDigits,
      }).format(amount)
    : ''

export const formatNumberToK = (
  amount: number,
  {
    currency = 'USD',
    round,
    locales = 'en-US',
  }: {
    currency?: string
    round?: boolean
    locales?: string | string[]
  } = {}
): string => {
  // Check if the amount is in thousands and format it
  if (amount >= 1000) {
    const thousands = amount / 1000
    // Use the currencyFormatter for the formatted thousands value, ensuring consistent currency formatting
    // Since currencyFormatter expects a number, we round the thousands value to get an appropriate format
    return currencyFormatter(thousands, { currency, round, locales }) + 'k'
  } else {
    // Use the currencyFormatter for amounts less than 1000
    return currencyFormatter(amount, { currency, round, locales })
  }
}

/**
 * Check if a URL is valid
 * @param url - The URL to check
 * @returns The URL object if valid, false otherwise
 */
export const isValidUrl = (url: string | null | undefined): URL | boolean => {
  if (!url) return false

  try {
    let urlToParse = url

    // if url does not start with http or https, validate it's a domain before prepending
    if (!url.startsWith('http:/') && !url.startsWith('https:/')) {
      // Special case: allow localhost
      const isLocalhost =
        url.startsWith('localhost') || url.includes('://localhost')

      if (!isLocalhost) {
        // Use tldts to check if this looks like a valid domain
        const parsed = parseDomain(url)

        // Only prepend https:// if we have a valid domain and TLD
        if (!parsed.domain || !parsed.publicSuffix) {
          return false
        }
      }

      urlToParse = `https://${url}`
    }

    // if url has invalid origin e.g https://.www. clean it up
    if (urlToParse.startsWith('https://.www.')) {
      urlToParse = urlToParse.replace('https://.www.', 'https://www.')
    }

    const urlObj = new URL(urlToParse)
    const isUrl =
      urlObj.hostname.indexOf('.') > -1 || urlObj.hostname === 'localhost'

    return isUrl ? urlObj : false
  } catch (_e) {
    return false
  }
}

/**
 * Extract the first URL from the text
 * @param text - The text to extract the URL from
 * @returns The URL if found, null otherwise
 */
export const extractUrl = (text?: string): URL | boolean => {
  if (!text) return false

  const url = text.match(/https?:\/\/[^\s]+/)?.[0]
  return isValidUrl(url)
}

export const isDupeUrl = (
  url?: string,
  currentHostname?: string
): URL | boolean => {
  if (!url) return false

  const valudUrl = isValidUrl(url)
  const selfHostnames = ['localhost', 'dupe.com']

  if (currentHostname) {
    selfHostnames.push(currentHostname)
  }

  const dupeUrl =
    valudUrl instanceof URL && selfHostnames.includes(valudUrl.hostname)

  return dupeUrl ? valudUrl : false
}

/**
 * Step 2 of the redirect pairing — enrich an EXISTING redirect URL.
 *
 * Takes any URL (a relative `/redirect?…`, an absolute `dupe.com/redirect/…`,
 * or a `dupe.link` short URL), resolves it to an ABSOLUTE URL (against
 * `window.location.origin`, or `https://dupe.com` server-side), and appends
 * tracking params + `clickout_url` (the source page, for affiliate attribution
 * / Trevor reports). Every write is guarded by `!searchParams.has(...)`, so it
 * is IDEMPOTENT — calling it on an already-enriched URL never double-writes a
 * param; it only fills gaps.
 *
 * Two jobs `convertToRedirectUrl`'s raw-URL path does NOT do: (1) absolutize
 * the relative `/redirect?…` it returns, and (2) add `clickout_url`. That's why
 * the canonical call-site idiom is the pair
 * `enrichRedirectUrl(convertToRedirectUrl(link, params), params)` — see
 * `convertToRedirectUrl` below. Passing the same `params` to both is safe
 * precisely because of the idempotency guards.
 */

/** `source_hash` carries meaning only on the paste pathway (it identifies the
 *  parsed source product), and the redirect controller drops values over 128
 *  chars. Apply the same gate at URL-construction time so generated links
 *  never carry attribution the server will ignore. */
const MAX_SOURCE_HASH_CHARS = 128
const resolvePasteSourceHash = (
  pathway: string | null | undefined,
  sourceHash: string | null | undefined
): string | null =>
  pathway === 'paste' &&
  typeof sourceHash === 'string' &&
  sourceHash.length > 0 &&
  sourceHash.length <= MAX_SOURCE_HASH_CHARS
    ? sourceHash
    : null

export const enrichRedirectUrl = (
  url: string,
  params?: {
    source?: string
    index?: number | null
    pick?: boolean
    key?: string | null
    hash?: string | null
    /** Request-level text-search impression identifier; never recompute. */
    textGridHash?: string | null
    // Raw user query that produced the result list. Persisted into
    // `link_clicks.metadata.q` so PostHog conversion events can be sliced
    // by what the user actually searched for — the missing axis for any
    // (query × retailer × product) ranking analysis.
    query?: string | null
    google_product_id?: string | null
    source_shop?: string | null
    shopping_source?: string | null
    google_product_token?: string | null
    google_uule?: string | null
    country?: string | null
    shopKey?: string | null
    partner?: boolean
    /** Set when the source row's `link` was swapped to AMP click_url
     *  (monetized:'amp'). Persisted to `link_clicks.metadata.monetized`
     *  via the redirect handler. Closed enum on the server (only 'amp'
     *  is accepted). */
    monetized?: 'amp' | null
    /** Default-sort arm the result list was rendered under. Persisted to
     *  `link_clicks.metadata.dsv` so commission (transactions ⨝ link_clicks)
     *  is sliceable by arm — the revenue guardrail for any experiment on the
     *  default sort. Its only producer was `rerank-default-sort`, removed on
     *  2026-09-16, so callers pass null today. */
    dsv?: string | null
    /** `dupe-search` (catalog_search_version) experiment arm the result list
     *  was retrieved under — the marqo↔PUMICE A/B. Persisted to
     *  `link_clicks.metadata.csv` so commission (transactions ⨝ link_clicks)
     *  is sliceable by retrieval arm. Null on Google-Shopping/Lens matches
     *  (not in the A/B). */
    csv?: string | null
    /** `affiliate-router` experiment arm. Persisted to
     *  `link_clicks.metadata.arv` so revenue can be sliced by router arm. */
    arv?: string | null
    /** Team-draft interleaving batch id (DUP-1022). Persisted to
     *  `link_clicks.metadata.ibid` — the key that groups a click back to the
     *  interleaved result set it came from. Null outside the harness. */
    ibid?: string | null
    /** Team-draft interleaving arm — which ranker contributed the clicked
     *  slot. Persisted to `link_clicks.metadata.ilarm`. Dropping this (or
     *  `ibid`) at any hop doesn't just lose one field: the click leaves the
     *  batch entirely and the per-arm win credit is understated. */
    ilarm?: 'a' | 'b' | null
    /** Sort lens the grid was on at click time. Persisted to
     *  `link_clicks.metadata.ilsc`; the readout drops clicks made on a
     *  re-sorted grid. Unlike `ibid`/`ilarm`, losing this at a hop is
     *  fail-safe — the click reads as unknown-lens and is excluded. */
    ilsc?: string | null
    /** Which duel the batch ran (DUP-1843): 'catalog' when arm B came from the
     *  own-catalog re-ranked lane, 'calibration' for the A-vs-degraded-A
     *  check. Persisted to `link_clicks.metadata.ilduel`. Two experiments share
     *  the batch-id space, so losing this pools them into one meaningless
     *  number — treat it as load-bearing as `ilarm`, not as a nice-to-have. */
    ilduel?: 'catalog' | 'catalog-fallback' | 'calibration' | null
    /** Input modality that produced the result list (text | image | url).
     *  Persisted to `link_clicks.metadata.input_type` so commission +
     *  conversions are sliceable per modality. */
    inputType?: 'text' | 'image' | 'url' | null
    /** v2 chat product-grid origin. Persisted as metadata.pathway. */
    pathway?: ChatResultPathway | null
    /** Source product hash for v2 chat paste attribution. */
    sourceHash?: string | null
    /** Commercial lane of the search that produced this grid
     *  (`furniture_home` | `fashion` | `beauty`), stamped as
     *  `router_category_group` so grid click-outs carry a category the way the
     *  toggler surface already does — see `ResultSortContext` for the full
     *  semantics. ⚠️ On the grid this is a CLASSIFICATION, not a routing
     *  decision: `router_version` / `router_merchant` stay null, and `src` is
     *  what separates the two populations. Already present in
     *  `ROUTER_REDIRECT_PARAM_KEYS`, so it survives every rebuild hop, and
     *  already validated server-side against `ROUTER_CATEGORY_GROUP_SET`. */
    routerCategoryGroup?: string | null
    /** Which CONTAINER the clicked card was rendered in (DUP-2048): `grid`,
     *  `rail`, `marketplace_tab` or `stack_drawer`. Persisted to
     *  `link_clicks.metadata.impression_source`.
     *
     *  ⚠️ The SURFACE, not the store — store identity is the top-level
     *  `shop_key` column. Per-store revenue is `GROUP BY shop_key`; this is
     *  what narrows it to one surface, which `link_clicks` could not do at all
     *  before (a grid click and a drawer click share `src`). Carried by
     *  `IMPRESSION_SOURCE_REDIRECT_PARAM_KEYS` so it survives every rebuild
     *  hop, and validated server-side against the shared `isImpressionSource`
     *  closed set.
     *
     *  ⚠️ Typed as the closed union rather than `string` so a caller cannot
     *  invent a surface; the API would discard it silently, and the click still
     *  routes, so nothing would look broken until a readout came back empty. */
    impressionSource?: ImpressionSource | null
    /** Source tagging: the retrieval leg that served the tile, its index in
     *  the list the server sent, and its PUMICE visual score. Persisted to
     *  `link_clicks.metadata.leg` / `.served_index` / `.visual_score` so
     *  commission can be cut per leg at a matched position. `idx` is the
     *  DISPLAYED index; the two differ whenever the client reorders. */
    leg?: ResultLeg | null
    servedIndex?: number | null
    /** Relevance-only slot (`VisualMatch.naturalIndex`), persisted to
     *  `link_clicks.metadata.natural_index`. */
    naturalIndex?: number | null
    visualScore?: number | null
    /** v2 chat thread id. Persisted to `link_clicks.metadata.thread_id`
     *  (DUP-1776 item 3) so the interleave readout joins a click to its chat
     *  thread as a first-class key instead of regexing it out of
     *  `clickout_url`. Not interleave-gated; validated + length-capped
     *  server-side (redirect.ts) since it rides the URL. */
    threadId?: string | null
    /** Fragrance answer id (the persisted assistant message id) and bottle
     *  (fragrance_items id), as `mid` / `iid` → `link_clicks.metadata.message_id`
     *  / `item_id` (tracking plan §6). Validated server-side. */
    messageId?: string | null
    itemId?: string | null
    /** Client-minted outbound click identifier (2026-08 lifecycle work).
     *  Persisted to `link_clicks.metadata.client_click_id`, and stamped on the
     *  matching `Dupe Result Clicked` analytics event as `click_id`. It exists
     *  because `link_clicks.short_id` is minted server-side at redirect time —
     *  AFTER the analytics event has already fired — so there is otherwise no
     *  identifier common to both rails. With it, PostHog joins to revenue:
     *  `click_id` → `metadata.client_click_id` → `short_id` →
     *  `strackr_transactions.lc_short_id`. Best-effort: minted after hydration,
     *  so a click landed before then simply carries none. */
    clickId?: string | null
    /** Where in a store drawer the card sat (`RetailerStackDrawer`). Stamped
     *  as `dsec` / `dslot` / `ssz` / `spos` / `doid` and persisted to
     *  `link_clicks.metadata.drawer_section` / `.section_slot` /
     *  `.stack_size` / `.stack_position` / `.drawer_open_id`, so commission
     *  can be cut by drawer section and slot. Null outside a drawer. Carried
     *  by `STACK_DRAWER_REDIRECT_PARAM_KEYS` through every rebuild hop. */
    stackDrawer?: StackDrawerPlacement | null
  },
  utmParams?: Record<string, string>
): string => {
  try {
    const urlObj = new URL(
      url,
      typeof window !== 'undefined'
        ? window.location.origin
        : 'https://dupe.com'
    )

    // Append tracking params if not already present
    if (params?.source && !urlObj.searchParams.has('src')) {
      urlObj.searchParams.set('src', params.source)
    }
    if (params?.index != null && !urlObj.searchParams.has('idx')) {
      urlObj.searchParams.set('idx', String(params.index))
    }
    if (params?.clickId && !urlObj.searchParams.has('cid')) {
      urlObj.searchParams.set('cid', params.clickId)
    }
    if (params?.hash && !urlObj.searchParams.has('hash')) {
      urlObj.searchParams.set('hash', params.hash)
    }
    if (params?.textGridHash) {
      urlObj.searchParams.set('tgh', params.textGridHash)
    }
    if (params?.pick && !urlObj.searchParams.has('pick')) {
      urlObj.searchParams.set('pick', '1')
    }
    if (params?.query && !urlObj.searchParams.has('q')) {
      // Match the server-side truncation in apps/api/src/controllers/redirect.ts.
      // Without this cap, the URL carries the full string but the DB stores
      // a truncated copy — `?q=<long>` on the URL is then unjoinable with
      // `link_clicks.metadata.q`, defeating the attribution this PR adds.
      urlObj.searchParams.set(
        'q',
        params.query.slice(0, MAX_REDIRECT_QUERY_LENGTH)
      )
    }
    if (params?.key && !urlObj.searchParams.has('key')) {
      const domain = getDomain(params.key)
      if (domain) {
        urlObj.searchParams.set('key', domain)
      }
    }
    if (params?.shopKey && !urlObj.searchParams.has('shopKey')) {
      urlObj.searchParams.set('shopKey', params.shopKey)
    }
    if (params?.partner && !urlObj.searchParams.has('partner')) {
      urlObj.searchParams.set('partner', '1')
    }
    if (params?.monetized && !urlObj.searchParams.has('monetized')) {
      urlObj.searchParams.set('monetized', params.monetized)
    }
    if (params?.dsv && !urlObj.searchParams.has('dsv')) {
      urlObj.searchParams.set('dsv', params.dsv)
    }
    if (params?.csv && !urlObj.searchParams.has('csv')) {
      urlObj.searchParams.set('csv', params.csv)
    }
    if (params?.arv && !urlObj.searchParams.has('arv')) {
      urlObj.searchParams.set('arv', params.arv)
    }
    // Interleave batch + arm travel as a pair; each is stamped independently
    // (same explicit-allowlist rule as `csv`) so a miss here shows up as a
    // click missing from the batch rather than a mis-attributed one.
    if (params?.ibid && !urlObj.searchParams.has('ibid')) {
      urlObj.searchParams.set('ibid', params.ibid)
    }
    if (params?.ilarm && !urlObj.searchParams.has('ilarm')) {
      urlObj.searchParams.set('ilarm', params.ilarm)
    }
    // Gated on `ibid` so the lens stamp never appears on a non-harness click.
    if (params?.ilsc && params?.ibid && !urlObj.searchParams.has('ilsc')) {
      urlObj.searchParams.set('ilsc', params.ilsc)
    }
    // Same gate: which of the two duels this batch ran.
    if (params?.ilduel && params?.ibid && !urlObj.searchParams.has('ilduel')) {
      urlObj.searchParams.set('ilduel', params.ilduel)
    }
    // v2 thread id → link_clicks.metadata.thread_id (DUP-1776 item 3). Not
    // interleave-gated; the server validates + caps it (redirect.ts).
    if (params?.threadId && !urlObj.searchParams.has('thread_id')) {
      urlObj.searchParams.set('thread_id', params.threadId)
    }
    if (params?.messageId && !urlObj.searchParams.has('mid')) {
      urlObj.searchParams.set('mid', params.messageId)
    }
    if (params?.itemId && !urlObj.searchParams.has('iid')) {
      urlObj.searchParams.set('iid', params.itemId)
    }
    if (params?.inputType && !urlObj.searchParams.has('input_type')) {
      urlObj.searchParams.set('input_type', params.inputType)
    }
    if (params?.pathway && !urlObj.searchParams.has('pathway')) {
      urlObj.searchParams.set('pathway', params.pathway)
    }
    // Grid click-outs carry the search's commercial lane. `!has` so a value
    // already on the URL from an earlier hop (or a real router decision) wins
    // — this must never overwrite a routed stamp.
    if (
      params?.routerCategoryGroup &&
      !urlObj.searchParams.has('router_category_group')
    ) {
      urlObj.searchParams.set(
        'router_category_group',
        params.routerCategoryGroup
      )
    }
    // Which store's surface the card came from (DUP-2048). `!has` for the same
    // reason as the stamp above: an earlier hop's value wins.
    if (
      params?.impressionSource &&
      !urlObj.searchParams.has('impression_source')
    ) {
      urlObj.searchParams.set('impression_source', params.impressionSource)
    }
    stampStackDrawerParams(urlObj.searchParams, params?.stackDrawer)
    stampSourceTagParams(urlObj.searchParams, params)
    const enrichSourceHash = resolvePasteSourceHash(
      params?.pathway,
      params?.sourceHash
    )
    if (enrichSourceHash && !urlObj.searchParams.has('source_hash')) {
      urlObj.searchParams.set('source_hash', enrichSourceHash)
    }

    // Append extension state so the redirect controller can persist
    // `metadata.extensionState` for revenue attribution. Reads from the
    // module-level snapshot published by `useExtensionState` so call sites
    // don't need to thread the value through explicitly.
    const extensionStateForUrl = getExtensionStateSnapshot()
    if (
      extensionStateForUrl?.installed &&
      !urlObj.searchParams.has('extension')
    ) {
      urlObj.searchParams.set(
        'extension',
        JSON.stringify({
          installed: true,
          version: extensionStateForUrl.version || '',
        })
      )
    }

    // Stamp the `signup-incentive-wall` arm from the module-level snapshot
    // (published only by the gated `SignupWallExperiment`, so this is null for
    // everyone outside the eligible population). Persists to
    // `link_clicks.metadata.swv` so affiliate commission is sliceable by arm.
    // `!has` so a value already forwarded onto the URL wins.
    const swvForUrl = getSignupWallVariantSnapshot()
    if (swvForUrl && !urlObj.searchParams.has('swv')) {
      urlObj.searchParams.set('swv', swvForUrl)
    }

    // Same for the generic experiment arms, published by whichever component
    // evaluated each allowlisted flag. Persists to `metadata.exp_arms`.
    const xaForUrl = getExperimentArmsParam()
    if (xaForUrl && !urlObj.searchParams.has(EXPERIMENT_ARM_PARAM)) {
      urlObj.searchParams.set(EXPERIMENT_ARM_PARAM, xaForUrl)
    }

    // Append UTM params
    if (utmParams) {
      Object.entries(utmParams).forEach(([key, value]) => {
        if (value && !urlObj.searchParams.has(key)) {
          urlObj.searchParams.set(key, value)
        }
      })
    }

    // Add clickout_url for web context
    if (
      typeof window !== 'undefined' &&
      !urlObj.searchParams.has('clickout_url')
    ) {
      urlObj.searchParams.set(
        'clickout_url',
        window.location.pathname + window.location.search
      )
    }

    // Remove mcp param - it's only for external MCP consumers, not our web/mobile clients
    urlObj.searchParams.delete('mcp')

    // Chat identifiers go into localStorage, not the page address — see
    // redirectChatContext.ts for why and for the fallbacks.
    moveChatContextOffRedirectUrl(urlObj)

    return urlObj.toString()
  } catch {
    return url // Return original if URL parsing fails
  }
}

// `ParsedUrlQuery` values are `string | string[] | undefined`. When the
// same key appears multiple times in a URL (`?q=a&q=b`), Next/router gives
// you `['a', 'b']`. Calling `.slice(0, N)` on the array would silently
// produce an array slice, which `URLSearchParams.set` then stringifies
// with a comma join — leaking unexpected values into the redirect URL.
// Normalize to the first defined string before any further processing.
const firstString = (
  v: string | string[] | undefined | null
): string | null => {
  if (Array.isArray(v)) return v[0] ?? null
  if (typeof v === 'string') return v
  return null
}

/** ⚠️ Every param `routerDecisionToRedirectQueryObject` (and the broadcast
 *  hook) emits MUST be listed here, or it is silently dropped on a URL rebuild
 *  hop and lands in `link_clicks.metadata` as null. The failure is invisible:
 *  the click still routes correctly and the other router params still arrive,
 *  so nothing looks broken until a readout comes back empty.
 *  This bit us on DUP-1927 — `router_eligibility_match` and
 *  `router_ashley_arm` shipped emitted-but-not-forwarded, and every Ashley
 *  click recorded null for both while `router_merchant` from the same emitter
 *  arrived fine. Adding a param is a TWO-file change. */
const ROUTER_REDIRECT_PARAM_KEYS = [
  'arv',
  'router_version',
  'router_category_group',
  'router_merchant',
  'router_confidence',
  'router_department',
  'router_fallback_reason',
  'router_eligibility_match',
  'router_ashley_arm',
] as const

/** Team-draft interleaving attribution for typed text search (DUP-1022):
 *  `ibid` identifies the interleaved batch, `ilarm` ('a' | 'b') the ranker
 *  that contributed the clicked slot. The batch is only scoreable if BOTH
 *  survive every rebuild hop — a click that arrives without them is not
 *  "unattributed", it silently drops out of the batch and biases the
 *  per-arm credit that decides which ranker wins.
 *  `ilsc` is the sort lens the grid was on at click time. It travels with the
 *  pair because the readout must DROP clicks made on a re-sorted grid: the
 *  Price / Match / VisualMatch lenses re-rank the whole list on an
 *  arm-independent key, which dissolves the A-vs-B comparison rather than
 *  merely biasing it. Losing `ilsc` on a hop is fail-safe (the click reads as
 *  unknown-lens and is excluded), unlike losing `ibid`/`ilarm`.
 *  `ilduel` says WHICH duel the batch ran ('catalog' | 'calibration'). It is
 *  as load-bearing as `ibid`/`ilarm`, not fail-safe like `ilsc`: two different
 *  experiments share the batch-id space and the catalog lane silently degrades
 *  into the calibration whenever it is skipped, so a click that loses this
 *  cannot be assigned to either, and pooling them reports a number for neither.
 */
const INTERLEAVE_REDIRECT_PARAM_KEYS = [
  'ibid',
  'ilarm',
  'ilsc',
  'ilduel',
] as const

/** Which CONTAINER the clicked card was rendered in (DUP-2048) — `grid`,
 *  `rail`, `marketplace_tab` or `stack_drawer`.
 *
 *  ⚠️ **The SURFACE, deliberately not the store.** Store identity already rides
 *  every `link_clicks` row as the top-level `shop_key` column, derived
 *  independently from the destination URL, so a param naming the store would
 *  duplicate it. What `link_clicks` genuinely could not answer is which SURFACE
 *  a click came from: an organic-grid click and a stack-drawer click are both
 *  `src: 'v2-product-list-card'` and were otherwise indistinguishable.
 *
 *  That is the whole business case. Revenue joins
 *  `strackr_transactions.lc_short_id = link_clicks.short_id`, so per-store
 *  earnings are `GROUP BY shop_key` — and narrowing that to the marketplaces
 *  surface, rather than the grid that is ~88% of click volume, is only possible
 *  with this column.
 *
 *  ⚠️ Its own group rather than a member of `ROUTER_REDIRECT_PARAM_KEYS`: those
 *  describe the affiliate router's ROUTING DECISION and the API reads them
 *  through `routerStampParam`, which prefers the server's own stamp over the
 *  client's. This is a client-only PLACEMENT stamp — the server has no opinion
 *  about which container a card rendered in — so it needs the plain client read.
 *
 *  ⚠️ A group, not a bare literal like `dsv`, so `REDIRECT_ATTRIBUTION_PARAMS`
 *  picks it up by spread. `dsv` is hand-listed in five places; that is the
 *  anti-pattern the comment on that list warns about, not a precedent. */
const IMPRESSION_SOURCE_REDIRECT_PARAM_KEYS = ['impression_source'] as const

/** Source-tagging params (`leg`, `sidx` served index, `nidx` natural
 *  relevance-only index, `vs` visual score). A
 *  group for the same reason as the one above: spread into
 *  `REDIRECT_ATTRIBUTION_PARAMS` and forwarded on every rebuild hop, so no
 *  builder can drop them silently. Validated server-side. */
const SOURCE_TAG_REDIRECT_PARAM_KEYS = ['leg', 'sidx', 'nidx', 'vs'] as const

/** The Customer.io recipient on an email link (`cio={{customer.cio_id}}`, or
 *  the `cio_id` Customer.io itself appends), so the API can credit the click
 *  to that profile. A group for the same reason as the ones above. See
 *  apps/api/src/lib/customerio/emailClick.ts. */
const EMAIL_RECIPIENT_REDIRECT_PARAM_KEYS = ['cio', 'cio_id'] as const

/** Stack drawer placement (`dsec` section, `dslot` slot within it, `ssz` stack
 *  size, `spos` the stack cell's grid position, `doid` the drawer-open id). A
 *  group for the same reason as the ones above: spread into
 *  `REDIRECT_ATTRIBUTION_PARAMS` and forwarded on every rebuild hop, so a
 *  drawer click cannot reach `link_clicks` without them while still routing.
 *  Short keys like `idx` / `sidx`; the API writes them under their full
 *  snake_case names. Validated server-side. */
/** The fragrance answer (`mid`) and bottle (`iid`) a buy link came from
 *  (tracking plan §6), forwarded on every rebuild hop like `thread_id`. */
const FRAGRANCE_ANSWER_REDIRECT_PARAM_KEYS = ['mid', 'iid'] as const

const STACK_DRAWER_REDIRECT_PARAM_KEYS = [
  'dsec',
  'dslot',
  'ssz',
  'spos',
  'doid',
] as const

/** Generic experiment arms (`xa=<flag>:<variant>,…`, see
 *  `@dupe/common/experimentArms`). A group for the same reason as the ones
 *  above: forwarded on every rebuild hop and spread into
 *  `REDIRECT_ATTRIBUTION_PARAMS`. Validated server-side against the allowlist. */
const EXPERIMENT_ARM_REDIRECT_PARAM_KEYS = [EXPERIMENT_ARM_PARAM] as const

/** `!has` so a value an earlier hop already wrote wins. */
function stampSourceTagParams(
  searchParams: URLSearchParams,
  params?: {
    leg?: ResultLeg | null
    servedIndex?: number | null
    naturalIndex?: number | null
    visualScore?: number | null
  }
): void {
  if (params?.leg && !searchParams.has('leg')) {
    searchParams.set('leg', params.leg)
  }
  if (typeof params?.servedIndex === 'number' && !searchParams.has('sidx')) {
    searchParams.set('sidx', String(params.servedIndex))
  }
  if (typeof params?.naturalIndex === 'number' && !searchParams.has('nidx')) {
    searchParams.set('nidx', String(params.naturalIndex))
  }
  if (typeof params?.visualScore === 'number' && !searchParams.has('vs')) {
    searchParams.set('vs', params.visualScore.toFixed(4))
  }
}

/** `!has` so a value an earlier hop already wrote wins. Numbers are checked
 *  with `typeof`, not truthiness: slot 0 and position 0 are the rows that
 *  matter most. */
function stampStackDrawerParams(
  searchParams: URLSearchParams,
  placement?: StackDrawerPlacement | null
): void {
  if (!placement) return
  const values: Record<
    (typeof STACK_DRAWER_REDIRECT_PARAM_KEYS)[number],
    string | null
  > = {
    dsec: placement.drawerSection,
    dslot:
      typeof placement.sectionSlot === 'number'
        ? String(placement.sectionSlot)
        : null,
    ssz:
      typeof placement.stackSize === 'number'
        ? String(placement.stackSize)
        : null,
    spos:
      typeof placement.stackPosition === 'number'
        ? String(placement.stackPosition)
        : null,
    doid: placement.drawerOpenId,
  }
  for (const key of STACK_DRAWER_REDIRECT_PARAM_KEYS) {
    const value = values[key]
    if (value && !searchParams.has(key)) searchParams.set(key, value)
  }
}

/** Copy a fixed group of already-stamped params off the page query onto a
 *  rebuilt redirect URL. `!has` so a value already written by an earlier
 *  hop wins. Values are client-forwarded and validated server-side. */
function forwardRedirectParamGroup(
  params: URLSearchParams,
  query: ParsedUrlQuery,
  keys: readonly string[]
): void {
  for (const key of keys) {
    const value = firstString(query?.[key])
    if (value && !params.has(key)) {
      params.set(key, value)
    }
  }
}

function forwardRouterRedirectParams(
  params: URLSearchParams,
  query: ParsedUrlQuery
): void {
  forwardRedirectParamGroup(params, query, ROUTER_REDIRECT_PARAM_KEYS)
}

function forwardInterleaveRedirectParams(
  params: URLSearchParams,
  query: ParsedUrlQuery
): void {
  forwardRedirectParamGroup(params, query, INTERLEAVE_REDIRECT_PARAM_KEYS)
}

function forwardSourceTagRedirectParams(
  params: URLSearchParams,
  query: ParsedUrlQuery
): void {
  forwardRedirectParamGroup(params, query, SOURCE_TAG_REDIRECT_PARAM_KEYS)
}

/** Called only by the `/redirect` page, never inside `buildRedirectLink`: the
 *  API credits every resolve that carries `cio`/`cio_id` as an email click, so a grid
 *  that pre-resolves its cards must never pick one up from a page URL. */
export function forwardEmailRecipientRedirectParams(
  params: URLSearchParams,
  query: ParsedUrlQuery
): void {
  forwardRedirectParamGroup(params, query, EMAIL_RECIPIENT_REDIRECT_PARAM_KEYS)
}

function forwardExperimentArmRedirectParams(
  params: URLSearchParams,
  query: ParsedUrlQuery
): void {
  forwardRedirectParamGroup(params, query, EXPERIMENT_ARM_REDIRECT_PARAM_KEYS)
}

function forwardImpressionSourceRedirectParams(
  params: URLSearchParams,
  query: ParsedUrlQuery
): void {
  forwardRedirectParamGroup(
    params,
    query,
    IMPRESSION_SOURCE_REDIRECT_PARAM_KEYS
  )
}

function forwardFragranceAnswerRedirectParams(
  params: URLSearchParams,
  query: ParsedUrlQuery
): void {
  forwardRedirectParamGroup(params, query, FRAGRANCE_ANSWER_REDIRECT_PARAM_KEYS)
}

function forwardStackDrawerRedirectParams(
  params: URLSearchParams,
  query: ParsedUrlQuery
): void {
  forwardRedirectParamGroup(params, query, STACK_DRAWER_REDIRECT_PARAM_KEYS)
}

// Note: `extension` is not extracted here — for the `link` path it is
// appended later from the module-level snapshot; for the `dupeLinkToken`
// path it is forwarded from `query` directly in `buildRedirectLink`.
export const getRedirectParams = (link: string, query: ParsedUrlQuery) => {
  const {
    src,
    hash,
    tgh,
    idx,
    pick,
    key,
    q,
    shopKey,
    monetized,
    dsv,
    csv,
    arv,
    swv,
    input_type,
    pathway,
    source_hash,
    thread_id,
    cid,
  } = query || {}
  const params = new URLSearchParams({
    link: link,
  })

  if (hash) {
    params.set('hash', hash as string)
  }

  forwardRedirectParamGroup(params, { tgh }, ['tgh'])

  if (pick) {
    params.set('pick', pick as string)
  }

  if (src) {
    params.set('src', src as string)
  }

  // Match the standard branch's nullish check so `idx=0` (top-slot click)
  // survives even if a future caller passes the raw number through.
  if (idx != null && idx !== '') {
    params.set('idx', idx as string)
  }

  const qStr = firstString(q)
  if (qStr) {
    params.set('q', qStr.slice(0, MAX_REDIRECT_QUERY_LENGTH))
  }

  if (key) {
    const domain = getDomain(key as string)
    if (domain) {
      params.set('key', domain)
    }
  }

  const shopKeyStr = firstString(shopKey)
  if (shopKeyStr) {
    params.set('shopKey', shopKeyStr)
  }

  const monetizedStr = firstString(monetized)
  if (monetizedStr === 'amp') {
    params.set('monetized', 'amp')
  }

  // Forward the default-sort arm (`dsv`) + input modality so the intermediate
  // `/redirect?...` page → final `/api/redirect` rebuild doesn't drop them.
  // Without this, normal result-card clicks (which route through this
  // query-based path) record `link_clicks.metadata.dsv` / `input_type` as
  // null, breaking the per-arm commission guardrail. `dsv` has had no producer
  // since `rerank-default-sort` was removed on 2026-09-16 — the forwarding
  // stays so the guardrail is intact for the next default-sort experiment, and
  // `input_type` is written on every click regardless. Server validates both.
  const dsvStr = firstString(dsv)
  if (dsvStr) {
    params.set('dsv', dsvStr)
  }
  const csvStr = firstString(csv)
  if (csvStr) {
    params.set('csv', csvStr)
  }
  const arvStr = firstString(arv)
  if (arvStr) {
    params.set('arv', arvStr)
  }
  const inputTypeStr = firstString(input_type)
  if (inputTypeStr) {
    params.set('input_type', inputTypeStr)
  }
  const pathwayStr = firstString(pathway)
  if (pathwayStr) {
    params.set('pathway', pathwayStr)
  }
  const sourceHashStr = resolvePasteSourceHash(
    pathwayStr,
    firstString(source_hash)
  )
  if (sourceHashStr) {
    params.set('source_hash', sourceHashStr)
  }
  // Carry the v2 thread id through the `/redirect?...` → `/api/redirect` rebuild
  // (DUP-1776 item 3) so query-path clicks also record
  // link_clicks.metadata.thread_id. Server validates + caps it.
  const threadIdStr = firstString(thread_id)
  if (threadIdStr) {
    params.set('thread_id', threadIdStr)
  }
  // Carry the client-minted click id through the `/redirect?...` →
  // `/api/redirect` rebuild. Dropping it here would strip `client_click_id`
  // from exactly the query-path clicks the result grid produces — i.e. from
  // the majority of clicks the join is meant to cover. Server validates shape.
  const cidStr = firstString(cid)
  if (cidStr) {
    params.set('cid', cidStr)
  }
  // Forward the `signup-incentive-wall` arm the same way (initial stamp comes
  // from the snapshot in convert/enrichRedirectUrl; this preserves it through
  // the `/redirect?...` → `/api/redirect` rebuild).
  const swvStr = firstString(swv)
  if (swvStr) {
    params.set('swv', swvStr)
  }

  forwardRouterRedirectParams(params, query)
  // Carry `ibid`/`ilarm` through the `/redirect?...` → `/api/redirect`
  // rebuild. Without this the interleaved click reaches the API stripped of
  // its batch, so the team-draft credit for that impression is never counted.
  forwardInterleaveRedirectParams(params, query)
  // Carry the container through the same rebuild. A stack-drawer click that
  // loses it lands in `link_clicks` indistinguishable from an organic-grid
  // click — they already share `src` — so the surface gets no credit for
  // revenue it earned.
  forwardImpressionSourceRedirectParams(params, query)
  forwardStackDrawerRedirectParams(params, query)
  forwardFragranceAnswerRedirectParams(params, query)
  forwardSourceTagRedirectParams(params, query)
  forwardExperimentArmRedirectParams(params, query)

  if (typeof window !== 'undefined') {
    params.set('clickout_url', redirectPageSourceUrl())
  }

  return params
}

/**
 * Step 1 of the redirect pairing — turn a product link into a dupe `/redirect`
 * URL carrying base attribution params (`src`, `key`, `idx`, `q`, …).
 *
 * Branches by input shape:
 *  - already a `/redirect/…` or `/redirect?link=…` URL → delegates to
 *    `enrichRedirectUrl` (so callers can pass pre-obfuscated `dupeLink=true`
 *    API URLs through this one function);
 *  - a `dupe.link` short URL → returned AS-IS (its tracking was baked in at
 *    short-link creation — do not append params);
 *  - a raw retailer URL → builds a RELATIVE `/redirect?link=…&src=…&key=…`.
 *
 * The raw-URL branch deliberately stops at a relative URL and omits
 * `clickout_url`. Call sites therefore wrap it in `enrichRedirectUrl` to
 * absolutize the URL and add `clickout_url`:
 *
 *   enrichRedirectUrl(convertToRedirectUrl(link, params), params)
 *
 * That pair is the standard idiom across the chat cards (PricingCard,
 * ProductContextBubble, ChatRankedProductsCard, …). `enrichRedirectUrl` is
 * idempotent, so re-passing the same `params` doesn't double-write anything.
 */
export const convertToRedirectUrl = (
  url: string | null | undefined,
  params?: {
    source?: string
    index?: number | null
    pick?: boolean
    key?: string | null
    hash?: string | null
    /** Request-level text-search impression identifier; never recompute. */
    textGridHash?: string | null
    // Raw user query that produced the result list. Persisted into
    // `link_clicks.metadata.q` so conversion events can be sliced by the
    // search term that drove the click. See enrichRedirectUrl for details.
    query?: string | null
    google_product_id?: string | null
    source_shop?: string | null
    shopping_source?: string | null
    google_product_token?: string | null
    google_uule?: string | null
    country?: string | null
    shopKey?: string | null
    partner?: boolean
    /** Set when the source row's `link` was swapped to AMP click_url
     *  (monetized:'amp'). Persisted to `link_clicks.metadata.monetized`
     *  via the redirect handler. Closed enum on the server (only 'amp'
     *  is accepted). */
    monetized?: 'amp' | null
    /** Default-sort arm the result list was rendered under. Persisted to
     *  `link_clicks.metadata.dsv` so commission (transactions ⨝ link_clicks)
     *  is sliceable by arm — the revenue guardrail for any experiment on the
     *  default sort. Its only producer was `rerank-default-sort`, removed on
     *  2026-09-16, so callers pass null today. */
    dsv?: string | null
    /** `dupe-search` (catalog_search_version) experiment arm the result list
     *  was retrieved under — the marqo↔PUMICE A/B. Persisted to
     *  `link_clicks.metadata.csv` so commission (transactions ⨝ link_clicks)
     *  is sliceable by retrieval arm. Null on Google-Shopping/Lens matches
     *  (not in the A/B). */
    csv?: string | null
    /** `affiliate-router` experiment arm. Persisted to
     *  `link_clicks.metadata.arv` so revenue can be sliced by router arm. */
    arv?: string | null
    /** Team-draft interleaving batch id (DUP-1022). Forwarded to
     *  `enrichRedirectUrl` on the pre-obfuscated `/redirect/{token}` branch
     *  below — the live path for API-returned links — as well as stamped on
     *  the raw-URL branch. Null outside the harness. */
    ibid?: string | null
    /** Team-draft interleaving arm ('a' | 'b') for the clicked slot. Travels
     *  with `ibid`; losing either at a hop drops the click from the batch
     *  rather than merely blanking a field. */
    ilarm?: 'a' | 'b' | null
    /** Sort lens the grid was on at click time. Persisted to
     *  `link_clicks.metadata.ilsc` and used by the readout to DROP clicks made
     *  on a re-sorted grid. Only stamped alongside `ibid`, so it adds nothing
     *  to the ~100% of redirect URLs outside the harness. */
    ilsc?: string | null
    /** Which duel the batch ran (DUP-1843). Only stamped alongside `ibid`.
     *  See `INTERLEAVE_REDIRECT_PARAM_KEYS` for why it is not optional. */
    ilduel?: 'catalog' | 'catalog-fallback' | 'calibration' | null
    /** Input modality that produced the result list (text | image | url).
     *  Persisted to `link_clicks.metadata.input_type` so commission +
     *  conversions are sliceable per modality. */
    inputType?: 'text' | 'image' | 'url' | null
    /** v2 chat product-grid origin. Persisted as metadata.pathway. */
    pathway?: ChatResultPathway | null
    /** Source product hash for v2 chat paste attribution. */
    sourceHash?: string | null
    /** Commercial lane of the search that produced this grid
     *  (`furniture_home` | `fashion` | `beauty`), stamped as
     *  `router_category_group` so grid click-outs carry a category the way the
     *  toggler surface already does — see `ResultSortContext` for the full
     *  semantics. ⚠️ On the grid this is a CLASSIFICATION, not a routing
     *  decision: `router_version` / `router_merchant` stay null, and `src` is
     *  what separates the two populations. Already present in
     *  `ROUTER_REDIRECT_PARAM_KEYS`, so it survives every rebuild hop, and
     *  already validated server-side against `ROUTER_CATEGORY_GROUP_SET`. */
    routerCategoryGroup?: string | null
    /** Which CONTAINER the clicked card was rendered in (DUP-2048): `grid`,
     *  `rail`, `marketplace_tab` or `stack_drawer`. Persisted to
     *  `link_clicks.metadata.impression_source`.
     *
     *  ⚠️ The SURFACE, not the store — store identity is the top-level
     *  `shop_key` column. Per-store revenue is `GROUP BY shop_key`; this is
     *  what narrows it to one surface, which `link_clicks` could not do at all
     *  before (a grid click and a drawer click share `src`). Carried by
     *  `IMPRESSION_SOURCE_REDIRECT_PARAM_KEYS` so it survives every rebuild
     *  hop, and validated server-side against the shared `isImpressionSource`
     *  closed set.
     *
     *  ⚠️ Typed as the closed union rather than `string` so a caller cannot
     *  invent a surface; the API would discard it silently, and the click still
     *  routes, so nothing would look broken until a readout came back empty. */
    impressionSource?: ImpressionSource | null
    /** Source tagging: the retrieval leg that served the tile, its index in
     *  the list the server sent, and its PUMICE visual score. Persisted to
     *  `link_clicks.metadata.leg` / `.served_index` / `.visual_score` so
     *  commission can be cut per leg at a matched position. `idx` is the
     *  DISPLAYED index; the two differ whenever the client reorders. */
    leg?: ResultLeg | null
    servedIndex?: number | null
    /** Relevance-only slot (`VisualMatch.naturalIndex`), persisted to
     *  `link_clicks.metadata.natural_index`. */
    naturalIndex?: number | null
    visualScore?: number | null
    /** v2 chat thread id. Persisted to `link_clicks.metadata.thread_id`
     *  (DUP-1776 item 3) so the interleave readout joins a click to its chat
     *  thread as a first-class key instead of regexing it out of
     *  `clickout_url`. Not interleave-gated; validated + length-capped
     *  server-side (redirect.ts) since it rides the URL. */
    threadId?: string | null
    /** Fragrance answer id (the persisted assistant message id) and bottle
     *  (fragrance_items id), as `mid` / `iid` → `link_clicks.metadata.message_id`
     *  / `item_id` (tracking plan §6). Validated server-side. */
    messageId?: string | null
    itemId?: string | null
    /** Client-minted outbound click identifier (2026-08 lifecycle work).
     *  Persisted to `link_clicks.metadata.client_click_id`, and stamped on the
     *  matching `Dupe Result Clicked` analytics event as `click_id`. It exists
     *  because `link_clicks.short_id` is minted server-side at redirect time —
     *  AFTER the analytics event has already fired — so there is otherwise no
     *  identifier common to both rails. With it, PostHog joins to revenue:
     *  `click_id` → `metadata.client_click_id` → `short_id` →
     *  `strackr_transactions.lc_short_id`. Best-effort: minted after hydration,
     *  so a click landed before then simply carries none. */
    clickId?: string | null
    /** Where in a store drawer the card sat (`RetailerStackDrawer`). Stamped
     *  as `dsec` / `dslot` / `ssz` / `spos` / `doid` and persisted to
     *  `link_clicks.metadata.drawer_section` / `.section_slot` /
     *  `.stack_size` / `.stack_position` / `.drawer_open_id`, so commission
     *  can be cut by drawer section and slot. Null outside a drawer. Carried
     *  by `STACK_DRAWER_REDIRECT_PARAM_KEYS` through every rebuild hop. */
    stackDrawer?: StackDrawerPlacement | null
  },
  utmParams?: Record<string, string>
): string => {
  if (!url && !params?.google_product_id) return ''

  // Bypass double redirect: if the URL is already a redirect URL or short link
  // This happens when the API returns obfuscated links with dupeLink=true (now the default)
  // For /redirect/ URLs: enrich with tracking params for Trevor reports and affiliate attribution
  // For dupe.link short URLs: return as-is (tracking params are captured at short link creation)
  if (url) {
    try {
      // Handle both absolute URLs and relative paths
      if (url.startsWith('/redirect/')) {
        return enrichRedirectUrl(url, params, utmParams)
      }
      // Handle relative /redirect?link= URLs (query-param based redirects)
      if (url.startsWith('/redirect?')) {
        return enrichRedirectUrl(url, params, utmParams)
      }
      const parsedUrl = new URL(url)
      // Check for /redirect/ pathname - enrich with tracking params
      if (parsedUrl.pathname.startsWith('/redirect/')) {
        return enrichRedirectUrl(url, params, utmParams)
      }
      // Check for /redirect pathname with query params (e.g., dupe.com/redirect?link=...)
      if (
        parsedUrl.pathname === '/redirect' &&
        parsedUrl.searchParams.has('link')
      ) {
        return enrichRedirectUrl(url, params, utmParams)
      }
      // For dupe.link short URLs - return as-is (no extra params needed)
      if (parsedUrl.hostname === 'dupe.link') {
        return url
      }
    } catch {
      // Not a valid URL, continue with normal processing
    }
  }

  try {
    const searchParams = new URLSearchParams({
      link: url || params?.google_product_id || '',
    })

    if (params?.partner) {
      searchParams.set('partner', '1')
    }

    if (params?.hash) {
      searchParams.set('hash', params.hash)
    }

    if (params?.textGridHash) {
      searchParams.set('tgh', params.textGridHash)
    }

    if (params?.source) {
      searchParams.set('src', params.source)
    }

    // Use a nullish check so `index: 0` (top-slot click on a zero-indexed
    // surface) is preserved. The previous truthy check collapsed position 0
    // to "no idx written", dropping rank attribution exactly where it matters
    // most — the row that earns the highest CTR.
    if (params?.index != null) {
      searchParams.set('idx', `${params.index}`)
    }

    // Client-minted click id → `link_clicks.metadata.client_click_id`. The
    // same value is stamped on the `Dupe Result Clicked` event, which is what
    // makes a PostHog click joinable to the commission it earned.
    if (params?.clickId) {
      searchParams.set('cid', params.clickId)
    }

    if (params?.pick) {
      searchParams.set('pick', `${params.pick}`)
    }

    if (params?.query) {
      // Match the server-side truncation in apps/api/src/controllers/redirect.ts.
      // See the enrichRedirectUrl branch above for rationale.
      searchParams.set('q', params.query.slice(0, MAX_REDIRECT_QUERY_LENGTH))
    }

    if (params?.key) {
      const domain = getDomain(params?.key as string)
      if (domain) {
        searchParams.set('key', domain)
      }
    }

    // if google_product_id is present, add it to the search params
    if (params?.google_product_id) {
      searchParams.set('google_product_id', params.google_product_id)
    } else {
      if (params?.google_product_token) {
        searchParams.set('google_product_token', params.google_product_token)
      }
    }

    if (params?.google_uule) {
      searchParams.set('google_uule', params.google_uule)
    }

    if (params?.source_shop) {
      searchParams.set('source_shop', params.source_shop)
    }

    if (params?.shopping_source) {
      searchParams.set('shopping_source', params.shopping_source)
    }

    if (params?.country) {
      searchParams.set('selected_country', params.country)
    }

    if (params?.monetized) {
      searchParams.set('monetized', params.monetized)
    }

    if (params?.dsv) {
      searchParams.set('dsv', params.dsv)
    }

    if (params?.csv) {
      searchParams.set('csv', params.csv)
    }

    if (params?.arv) {
      searchParams.set('arv', params.arv)
    }

    // Interleave batch + arm (DUP-1022). Stamped explicitly like `csv` —
    // nothing rides through generically, so a builder that forgets these
    // silently drops its clicks out of the batch being scored.
    if (params?.ibid) {
      searchParams.set('ibid', params.ibid)
    }

    if (params?.ilarm) {
      searchParams.set('ilarm', params.ilarm)
    }

    // Gated on `ibid` so the lens stamp never appears on a non-harness click.
    if (params?.ilsc && params?.ibid) {
      searchParams.set('ilsc', params.ilsc)
    }

    // Same gate: which of the two duels this batch ran.
    if (params?.ilduel && params?.ibid) {
      searchParams.set('ilduel', params.ilduel)
    }

    // v2 thread id → link_clicks.metadata.thread_id (DUP-1776 item 3).
    if (params?.threadId) {
      searchParams.set('thread_id', params.threadId)
    }
    if (params?.messageId) {
      searchParams.set('mid', params.messageId)
    }
    if (params?.itemId) {
      searchParams.set('iid', params.itemId)
    }

    if (params?.inputType) {
      searchParams.set('input_type', params.inputType)
    }

    if (params?.pathway) {
      searchParams.set('pathway', params.pathway)
    }

    // Same stamp on the raw-URL branch. Chat grid links are always the
    // obfuscated `/redirect/<token>` form and take `enrichRedirectUrl` above,
    // but drawer/carousel surfaces can reach here — stamping only one branch
    // is the silent-null failure `ROUTER_REDIRECT_PARAM_KEYS` warns about.
    if (params?.routerCategoryGroup) {
      searchParams.set('router_category_group', params.routerCategoryGroup)
    }
    // Same stamp on the raw-URL branch, and for the same reason — a stack
    // drawer reaches here, and stamping only the obfuscated branch is exactly
    // the silent-null failure described above.
    if (params?.impressionSource) {
      searchParams.set('impression_source', params.impressionSource)
    }
    stampStackDrawerParams(searchParams, params?.stackDrawer)
    stampSourceTagParams(searchParams, params)

    const convertSourceHash = resolvePasteSourceHash(
      params?.pathway,
      params?.sourceHash
    )
    if (convertSourceHash) {
      searchParams.set('source_hash', convertSourceHash)
    }

    // Stamp the `signup-incentive-wall` arm from the module-level snapshot
    // (null outside the gated eligible population). Persists to
    // `link_clicks.metadata.swv` so commission is sliceable by arm.
    const swvForUrl = getSignupWallVariantSnapshot()
    if (swvForUrl) {
      searchParams.set('swv', swvForUrl)
    }
    const xaForUrl = getExperimentArmsParam()
    if (xaForUrl) {
      searchParams.set(EXPERIMENT_ARM_PARAM, xaForUrl)
    }

    // Append extension state so the redirect controller can persist
    // `metadata.extensionState` for revenue attribution. Reads from the
    // module-level snapshot published by `useExtensionState`.
    const extensionStateForUrl = getExtensionStateSnapshot()
    if (extensionStateForUrl?.installed) {
      searchParams.set(
        'extension',
        JSON.stringify({
          installed: true,
          version: extensionStateForUrl.version || '',
        })
      )
    }

    // append UTM params
    Object.entries(utmParams || {}).forEach(([key, value]) => {
      searchParams.set(key, value)
    })

    const relative = `/redirect?${searchParams.toString()}`
    if (typeof window === 'undefined') return relative
    const moved = new URL(relative, window.location.origin)
    moveChatContextOffRedirectUrl(moved)
    return moved.pathname + moved.search
  } catch (_e) {
    // use the original url
    return url || params?.google_product_id || ''
  }
}

export const cleanPhoneNumber = (phoneNumber: string): string => {
  const cleanNumber = phoneNumber.replace(/\D/g, '')
  const safeNumber = `+1${cleanNumber}`
  return safeNumber
}

export const fixUrl = (url?: string): string => {
  if (!url) return ''

  // Collapse a doubled scheme (any combination — `https://http://`,
  // `http://https://`, etc.) BEFORE the validity guard below: a doubled
  // scheme parses "successfully" via `new URL()` but with hostname
  // `http`/`https` — `isValidUrl` correctly rejects that as not a real host,
  // which used to make this function blank the image instead of repairing
  // it. Fixing the string first means validity is checked against the real
  // host.
  const deduped = stripDoubledProtocol(url)

  if (!isValidUrl(deduped)) return ''

  // if url starts with //, add https:// to the beginning
  if (deduped.startsWith('//')) {
    return `https:${deduped}`
  }

  const normalized = normalizeProductImageUrl(deduped)
  return normalized ?? deduped
}

export const extractPriceFromResult = (
  resultsData: Partial<ResultsResp> | null
) => {
  if (!resultsData) return { price: null, currency: 'USD' }

  const dataDetected = resultsData?.dataDetected || {}
  const dataDetectedPrice = dataDetected?.price
  const dataDetectedCurrency = dataDetected?.currency

  const pricing = resultsData?.pricing || {}
  const pricingPrice = pricing ? Number(pricing?.price) : undefined
  const pricingCurrency = pricing?.currency

  const price = pricingPrice || dataDetectedPrice
  const currency = pricingCurrency || dataDetectedCurrency

  return {
    price,
    currency,
  }
}

export const getNext2IoDomain = (url: string): string => {
  const NEXT2_IO_DOMAINS: string[] = ['target.com', 'amazon.com']

  // If not a next2.io URL, use the standard getDomain function
  if (!url.includes('next2.io')) {
    return getDomain(url) || ''
  }

  // Check for mapped domains in the URL
  for (const domain of NEXT2_IO_DOMAINS) {
    if (url.includes(domain)) {
      return domain
    }
  }

  // Default fallback to url domain
  return getDomain(url) || ''
}

/**
 * Estimates reading time in minutes based on word count, stripping HTML tags.
 * @param text The input text (can include HTML).
 * @param wpm Words per minute reading speed (default: 200).
 * @returns Estimated reading time in minutes.
 */
export function getReadingTime(text: string, wpm = 200): number {
  if (!text) return 0
  // Basic strip HTML tags
  const plainText = text.replace(/<[^>]*>/g, '')
  const words = plainText.trim().split(/\s+/).length
  return Math.ceil(words / wpm)
}

export function buildRedirectLink({
  link,
  dupeLinkToken,
  query,
  utmParams = {},
  uidOverride,
  fromMobileApp,
  mobileAppVersion,
  DUPE_API_URL,
  shoppingToken,
}: {
  link: string | null
  query: any
  utmParams?: Record<string, string>
  uidOverride?: string
  fromMobileApp?: boolean
  mobileAppVersion?: string
  DUPE_API_URL: string
  dupeLinkToken?: string | null
  shoppingToken?: string | null
}): URL {
  // `thread_id` arrives as `rcc` on links built by moveChatContextOffRedirectUrl.
  query = withRestoredChatContext(query)
  // Bypass double redirect: if the link is already a redirect URL or short link
  // This happens when the API returns obfuscated links with dupeLink=true (now the default)
  // For /redirect/ URLs: enrich with tracking params for Trevor reports and affiliate attribution
  // For dupe.link short URLs: return as-is (tracking params are captured at short link creation)
  if (link) {
    try {
      const parsedUrl = new URL(link)
      // Check for /redirect/ pathname - enrich with tracking params
      if (parsedUrl.pathname.startsWith('/redirect/')) {
        // Carry the route's `xa` onto the link BEFORE enriching, so it beats
        // the browser snapshot `enrichRedirectUrl` would otherwise stamp:
        // precedence is link stamp → route stamp → snapshot, and a rebuild
        // must never change an arm or drop one.
        let linkToEnrich = link
        const routeXa = firstString(query?.[EXPERIMENT_ARM_PARAM])
        if (routeXa && !parsedUrl.searchParams.has(EXPERIMENT_ARM_PARAM)) {
          parsedUrl.searchParams.set(EXPERIMENT_ARM_PARAM, routeXa)
          linkToEnrich = parsedUrl.toString()
        }
        const enrichedUrl = enrichRedirectUrl(linkToEnrich, {
          hash: query?.hash as string,
          textGridHash: firstString(query?.tgh),
          source: query?.src as string,
          // Use a nullish check so `idx=0` (top-slot click on a zero-indexed
          // surface) is preserved. The previous `query?.idx ? ...` test
          // collapsed the legitimate string "0" to null, dropping position-0
          // attribution exactly where it matters most.
          index:
            query?.idx != null && query?.idx !== ''
              ? parseInt(query.idx as string)
              : null,
          pick: query?.pick === '1',
          key: query?.key as string,
          // Forward the raw search query through the `/redirect/TOKEN?q=...`
          // path so PostHog conversion events for pre-obfuscated links carry
          // the query alongside the other tracking params. Without this,
          // `metadata.q` lands as null for the default link flow.
          // `firstString` collapses the `?q=a&q=b` array case before the
          // value enters the enrichRedirectUrl chain.
          query: firstString(query?.q),
          shopKey: firstString(query?.shopKey),
          // Forward the experiment arm + modality through the obfuscated-link
          // path too, mirroring the query-based branch below.
          dsv: firstString(query?.dsv),
          csv: firstString(query?.csv),
          arv: firstString(query?.arv),
          inputType: firstString(query?.input_type) as
            | 'text'
            | 'image'
            | 'url'
            | undefined,
          pathway: firstString(query?.pathway) as ChatResultPathway | undefined,
          sourceHash: firstString(query?.source_hash),
          // On this `/redirect/TOKEN` → `/api/redirect` rebuild `query` is the
          // only source for the thread id (initial build stamps it from the
          // match card) — mirror the dsv/pathway forwarding above (DUP-1776).
          threadId: firstString(query?.thread_id),
          // Same shape, and the one this branch was missing (DUP-2051). The
          // client mints `cid` at ACTIVATION and stamps it onto the anchor, so
          // it can only ever arrive here in `query` — there is nowhere else to
          // recover it from. Dropping it writes `client_click_id` null and the
          // PostHog→revenue join for that click is gone.
          clickId: firstString(query?.cid),
        })
        const enrichedUrlObj = new URL(enrichedUrl)
        // Forward the signup-incentive-wall arm from the page URL. On an initial
        // build `enrichRedirectUrl` stamps it from the snapshot; on the
        // `/redirect/...` → `/api/redirect` rebuild the snapshot is empty, so we
        // carry `swv` over from `query` (mirrors the `dsv` forwarding above).
        const swvStr = firstString(query?.swv)
        if (swvStr && !enrichedUrlObj.searchParams.has('swv')) {
          enrichedUrlObj.searchParams.set('swv', swvStr)
        }
        forwardRouterRedirectParams(enrichedUrlObj.searchParams, query)
        // Carry the interleave batch + arm over from the page URL. On the
        // initial build `convertToRedirectUrl`/`enrichRedirectUrl` stamp them
        // from the match; on this `/redirect/TOKEN` → `/api/redirect` rebuild
        // `query` is the only source, and losing them here drops the click
        // from the batch (mirrors the `dsv`/`swv` forwarding above).
        forwardInterleaveRedirectParams(enrichedUrlObj.searchParams, query)
        // Same for the container: on this rebuild `query` is the only source.
        forwardImpressionSourceRedirectParams(
          enrichedUrlObj.searchParams,
          query
        )
        forwardStackDrawerRedirectParams(enrichedUrlObj.searchParams, query)
        forwardFragranceAnswerRedirectParams(enrichedUrlObj.searchParams, query)
        forwardSourceTagRedirectParams(enrichedUrlObj.searchParams, query)
        forwardExperimentArmRedirectParams(enrichedUrlObj.searchParams, query)
        // Add shoppingToken if provided separately (not already in the link)
        if (
          shoppingToken &&
          !enrichedUrlObj.searchParams.has('shoppingToken')
        ) {
          enrichedUrlObj.searchParams.set('shoppingToken', shoppingToken)
        }
        return enrichedUrlObj
      }
      // For dupe.link short URLs - return as-is (no extra params needed)
      if (parsedUrl.hostname === 'dupe.link') {
        return parsedUrl
      }
    } catch {
      // Not a valid URL, continue with normal processing
    }
  }

  let params = new URLSearchParams()
  if (link) {
    params = getRedirectParams(link, query)
  } else if (dupeLinkToken) {
    params.set('dupeLinkToken', dupeLinkToken as string)

    // Add tracking params for link_clicks attribution - consistent with getRedirectParams
    const {
      src,
      hash,
      tgh,
      idx,
      pick,
      key,
      q,
      shopKey,
      dsv,
      csv,
      arv,
      swv,
      input_type,
      pathway,
      source_hash,
      thread_id,
      cid,
    } = query || {}
    if (hash) {
      params.set('hash', hash as string)
    }
    forwardRedirectParamGroup(params, { tgh }, ['tgh'])
    if (pick) {
      params.set('pick', pick as string)
    }
    if (src) {
      params.set('src', src as string)
    }
    // Match getRedirectParams: forward the literal string "0" too so a
    // top-slot click on a zero-indexed surface still carries `idx`.
    if (idx != null && idx !== '') {
      params.set('idx', idx as string)
    }
    // Mirror the same forwarding `getRedirectParams` does for the `link`
    // branch. Without this, pre-obfuscated `/redirect/TOKEN?q=...` URLs
    // build `/api/redirect?dupeLinkToken=TOKEN&...` with `q` dropped, so
    // `metadata.q` lands as null and the new PostHog query attribution is
    // missing for the default pre-obfuscated link flow.
    const qStr = firstString(q)
    if (qStr) {
      params.set('q', qStr.slice(0, MAX_REDIRECT_QUERY_LENGTH))
    }
    if (key) {
      const domain = getDomain(key as string)
      if (domain) {
        params.set('key', domain)
      }
    }
    const shopKeyStr = firstString(shopKey)
    if (shopKeyStr) {
      params.set('shopKey', shopKeyStr)
    }
    // Mirror the `link` branch: forward the experiment arm + modality so
    // pre-obfuscated `dupeLinkToken` clicks also populate
    // `link_clicks.metadata.dsv` / `input_type`.
    const dsvStr = firstString(dsv)
    if (dsvStr) {
      params.set('dsv', dsvStr)
    }
    const csvStr = firstString(csv)
    if (csvStr) {
      params.set('csv', csvStr)
    }
    const arvStr = firstString(arv)
    if (arvStr) {
      params.set('arv', arvStr)
    }
    const inputTypeStr = firstString(input_type)
    if (inputTypeStr) {
      params.set('input_type', inputTypeStr)
    }
    const pathwayStr = firstString(pathway)
    if (pathwayStr) {
      params.set('pathway', pathwayStr)
    }
    const sourceHashStr = resolvePasteSourceHash(
      pathwayStr,
      firstString(source_hash)
    )
    if (sourceHashStr) {
      params.set('source_hash', sourceHashStr)
    }
    // Mirror the `link` branch (getRedirectParams): forward the v2 chat
    // thread id so pre-obfuscated `dupeLinkToken` clicks — the main redirect
    // flow — also populate `link_clicks.metadata.thread_id` (DUP-1776 item 3).
    // Server validates + caps it (`sanitizedShortParam`).
    const threadIdStr = firstString(thread_id)
    if (threadIdStr) {
      params.set('thread_id', threadIdStr)
    }
    // THE DUP-2051 FIX. This branch's opening comment claims it is "consistent
    // with getRedirectParams", and for every param above it is — but `cid` was
    // never on the list, while `getRedirectParams` has forwarded it since it
    // shipped. Chat result links are ALWAYS the obfuscated `/redirect/<token>`
    // form (product links are obfuscated for security), so every chat-grid
    // click took this branch and arrived at `/api/redirect` with no `cid`:
    // measured 0 of 94,631 token-form clicks kept their id, against 3,506 of
    // 3,506 on the query form. Not a rate — a gate.
    // The id rides as a sibling query param because the token encodes only the
    // destination URL, so there is no other channel for it.
    const cidStr = firstString(cid)
    if (cidStr) {
      params.set('cid', cidStr)
    }
    // Forward the `signup-incentive-wall` arm too, consistent with the `link`
    // branch, so pre-obfuscated `dupeLinkToken` clicks also populate
    // `link_clicks.metadata.swv`.
    const swvStr = firstString(swv)
    if (swvStr) {
      params.set('swv', swvStr)
    }
    forwardRouterRedirectParams(params, query)
    // Mirror the `link` branch so pre-obfuscated `dupeLinkToken` clicks also
    // populate `link_clicks.metadata.ibid` / `ilarm` — a click that loses them
    // here is missing from the interleave batch, not just untagged.
    forwardInterleaveRedirectParams(params, query)
    // Carry the container through the same rebuild. A stack-drawer click that
    // loses it lands in `link_clicks` indistinguishable from an organic-grid
    // click — they already share `src` — so the surface gets no credit for
    // revenue it earned.
    forwardImpressionSourceRedirectParams(params, query)
    forwardStackDrawerRedirectParams(params, query)
    forwardFragranceAnswerRedirectParams(params, query)
    forwardSourceTagRedirectParams(params, query)
    forwardExperimentArmRedirectParams(params, query)
    // Add clickout_url for web context - consistent with getRedirectParams
    if (typeof window !== 'undefined') {
      params.set('clickout_url', redirectPageSourceUrl())
    }
    // Forward extension attribution from the page URL (e.g.
    // /redirect/TOKEN?extension=...) so the API controller can persist it
    // in link_clicks metadata.
    if (query?.extension) {
      params.set('extension', query.extension as string)
    }
  }
  // Add google product token with params object
  if (shoppingToken) {
    params.set('shoppingToken', shoppingToken)
  }
  const redirectLink = new URL(`${DUPE_API_URL}/api/redirect?${params}`)

  // append UTM params
  Object.entries(utmParams || {}).forEach(([key, value]) => {
    redirectLink.searchParams.set(key, value)
  })

  // append extension info (reads from the module-level snapshot published
  // by `useExtensionState`)
  const extensionStateForUrl = getExtensionStateSnapshot()
  if (extensionStateForUrl?.installed) {
    redirectLink.searchParams.set(
      'extension',
      JSON.stringify({
        installed: true,
        version: extensionStateForUrl.version || '',
      })
    )
  }

  // append uidOverride
  if (uidOverride) {
    redirectLink.searchParams.set('uid-mobile', uidOverride)
  }

  // append mobile app info
  if (fromMobileApp) {
    redirectLink.searchParams.set('ref', 'mobile-app')
    redirectLink.searchParams.set('mobile-version', mobileAppVersion || '')
  }

  return redirectLink
}

/**
 * Return the `/api/redirect` URL with `rh=1` set, so the API responds with a
 * `302` to the resolved, affiliate-wrapped destination instead of the plain-text
 * URL body it returns by default.
 *
 * Used by the `/redirect/{token}` interstitial as its fallback when the
 * client-side resolver fetch fails (blocked by an extension, timed out, or an
 * HTTP error). Without `rh=1`, the old fallback navigated the tab straight to
 * the bare resolver URL, which `/api/redirect` answers as `text/plain` — the
 * "raw URL shown instead of redirecting" symptom. With `rh=1`, a top-level
 * navigation follows a real `302` to the retailer, and resolution + affiliate
 * wrap + the `link_clicks` write all happen server-side.
 *
 * Idempotent: sets `rh=1` whether or not it was already present. See the
 * server contract in `apps/api/src/controllers/redirect.ts` (`rh === '1'` → 302).
 */
export const withServerRedirectHeader = (redirectLink: URL): string => {
  const u = new URL(redirectLink.toString())
  u.searchParams.set('rh', '1')
  return u.toString()
}

/**
 * The destination, as an EITHER/OR. `redirect.ts:376-378` reads
 * `dupeLinkToken` and falls back to `link`, and the token decodes to the link —
 * so one is enough and carrying both only re-adds bulk. First hit wins.
 *
 * ⚠️ THERE IS A THIRD WAY to establish a destination and it is NOT handled
 * here, on purpose — but read this before wiring it up. Server-side toggler
 * routing (`redirect.ts:397-413`) resolves a destination from `rr=1` plus
 * `rr_q` / `rr_cat` / `rr_conf` / `rr_dept` / `rr_rug` / `rr_ashley`
 * (`routerConfig/resolveRequest.ts:87-108`), and it runs BEFORE the
 * "Link is required" guard at `:416` — so it is a peer of the two params above,
 * not a refinement of them.
 *
 * The client half now exists (`useRedirectUrl`'s `serverResolve`, behind the
 * `toggler-server-routing` flag), and it still never reaches this function with
 * an `rr=1` URL: a failed server-routed request (431 included) falls back to the
 * plain `?link=` request for the locally-built URL FIRST, and only that request's
 * failure goes through `pickResolverRecovery` / compaction. So the gap below is
 * unreachable from the web client as wired. ⚠️ Anyone adding a second `rr=1`
 * sender that hands its URL to recovery directly re-opens it: a 431 would
 * compact to `null` and dead-end on the invalid-link UI — the same silent
 * failure `shoppingToken` had, for the same reason: a resolution mechanism this
 * function has no concept of.
 *
 * Not pre-emptively added because it is a param GROUP rather than a single
 * either/or param, so it needs its own arm rather than a list entry. The gap is pinned by a
 * characterisation test in `helpers.spec.ts` ("rr=1 toggler URLs are a known
 * dormant gap") which asserts today's `null` and will fail the moment someone
 * changes this behaviour without revisiting the comment.
 */
const REDIRECT_PRIMARY_RESOLUTION_PARAMS = ['dupeLinkToken', 'link'] as const

/**
 * Resolution params that are COMPANIONS, not alternatives — carried in
 * addition to the primary, always, and never subject to the length budget.
 *
 * ⛔ `shoppingToken` must never be treated as an alternative to
 * `dupeLinkToken`. For a Google-Shopping product the token decodes to a Google
 * *intermediary* URL, and `redirect.ts` needs `shoppingToken` to turn that into
 * the real retailer (`effectiveShoppingToken` → `resolveGoogleProductUrl`,
 * :829-857). Drop it and `isGoogleIntermediaryUrl(link)` is true, so the
 * controller falls through to `getSmartFallbackUrl` (:882) — a keyword SEARCH
 * page, or an expired `aclk`. The shopper lands somewhere plausible-looking
 * that isn't the item they clicked.
 *
 * ⚠️ And that is exactly the cohort compaction exists for: `shoppingToken` runs
 * ~2.5 KB and appears twice (top level, plus inside the nested `clickout_url`
 * that `preservedParams` reads as a fallback), so it is the dominant
 * contributor to the 7-8 KB that trips a 431. A non-Google URL rarely gets
 * oversized at all. An earlier version of this function `break`ed on the first
 * resolution hit and shed `shoppingToken` — i.e. it got under budget precisely
 * BY breaking the shoppers it was meant to rescue. Caught in review, not by a
 * test, because no case carried both tokens.
 */
const REDIRECT_COMPANION_RESOLUTION_PARAMS = ['shoppingToken'] as const

/**
 * Attribution params worth carrying onto a compacted retry, MOST valuable
 * first. Everything here lands in `link_clicks.metadata`, and the ones near the
 * top are what revenue gets sliced by — so if the budget runs out, it runs out
 * on the least consequential fields.
 *
 * ⛔ `clickout_url` is deliberately absent, and that omission is the entire
 * point of this function. Measured on a real production redirect URL it was
 * **340 of 702 bytes — 48% of the whole thing** — because it nests a COPY of
 * the redirect URL, including that URL's own token and its own `clickout_url`.
 * That recursion is what compounds these to 7-8 KB. It is also the least
 * valuable field here: a UI return path, not revenue attribution.
 *
 * ⚠️ The experiment groups are SPREAD from the same constants the rebuild hops
 * forward, never re-typed as string literals. `ROUTER_REDIRECT_PARAM_KEYS`
 * carries the warning that adding a router param is a two-file change; listing
 * its members again here would quietly make it three, and the third omission is
 * the invisible kind — the click still routes, so nothing looks broken until an
 * experiment readout comes back empty (the DUP-1927 shape). Spreading keeps a
 * new param carried automatically. `arv` reaches this list via that spread,
 * which is why it no longer appears as a literal.
 */
const REDIRECT_ATTRIBUTION_PARAMS = [
  // Revenue slicers — what commission and surface performance get cut by.
  'src',
  'key',
  'idx',
  'dsv',
  'csv',
  'swv',
  'input_type',
  'hash',
  'tgh',
  'shopKey',
  'pathway',
  'thread_id',
  'cid',
  ...FRAGRANCE_ANSWER_REDIRECT_PARAM_KEYS,
  // Experiment attribution, ahead of `q`/`utm_*` on purpose: these decide which
  // ranker or router arm gets credit, and losing one does not merely leave a
  // click unattributed — it drops the click out of its batch and biases the
  // per-arm comparison. They are also short, so they rarely cost anything.
  ...ROUTER_REDIRECT_PARAM_KEYS,
  ...INTERLEAVE_REDIRECT_PARAM_KEYS,
  ...IMPRESSION_SOURCE_REDIRECT_PARAM_KEYS,
  ...STACK_DRAWER_REDIRECT_PARAM_KEYS,
  ...SOURCE_TAG_REDIRECT_PARAM_KEYS,
  ...EMAIL_RECIPIENT_REDIRECT_PARAM_KEYS,
  ...EXPERIMENT_ARM_REDIRECT_PARAM_KEYS,
  // Least consequential last — a long `q` should run out of budget before
  // anything above it does.
  'q',
  'utm_source',
  'utm_medium',
  'utm_campaign',
] as const

/**
 * Length budget governing how much ATTRIBUTION rides along. It is not a cap on
 * the finished URL: resolution params are carried unconditionally, so a URL
 * whose `shoppingToken` alone exceeds this still keeps it and simply carries no
 * attribution.
 *
 * ⚠️ A MARGIN, not a measured threshold. The real limit is not a URL length at
 * all — a 431 fires when the request LINE plus all headers exceed the server's
 * cap, so a shopper with large cookies trips it at a shorter URL than one
 * without. What we know empirically is that ~7-8 KB URLs do fail.
 *
 * 1 KB comfortably fits a plain token (~120 bytes) plus every attribution field
 * measured on a real URL (~160 bytes), so nothing valuable gets dropped on the
 * common shape. A Google-Shopping URL lands at ~3-4 KB instead, because its
 * ~2.5 KB `shoppingToken` is required — still roughly halving the original by
 * shedding the nested duplicate copy, which is the win actually available
 * there. Getting that cohort genuinely small needs the source-level
 * `clickout_url` de-duplication, not a bigger hammer here.
 *
 * Widen this only with evidence, and never past the point where the retry could
 * itself 431 — a retry that re-fails puts the shopper exactly where this code
 * exists to rescue them from.
 */
export const COMPACT_REDIRECT_MAX_LENGTH = 1000

/**
 * Rebuild a redirect URL small enough that request SIZE cannot be the failure
 * again, keeping as much attribution as fits. Returns `null` when there is no
 * resolution param to rebuild from — the caller must not navigate then.
 *
 * **Why this exists.** A redirect URL can reach ~7-8 KB because it nests a
 * whole `clickout_url` (which itself nests another). Past the server's cap the
 * request is rejected on size with a 431 *before any handler runs*, and the
 * ordinary `rh=1` recovery cannot help — it re-sends that same URL five bytes
 * longer and re-blocks. Dropping the nesting takes a real production URL from
 * 702 bytes to ~360 while keeping every attribution field, so the shopper
 * reaches the retailer, the affiliate wrap still happens server-side, and the
 * click stays sliceable.
 *
 * ⚠️ Only ever call this on a 431. On any other failure the full URL works and
 * carries strictly more, so compacting would throw away attribution for
 * nothing.
 *
 * ⚠️ `recovered: true` on this path is an argument from MARGIN, not an
 * observation — once the tab navigates we cannot see whether the retry
 * succeeded. It holds because ~360 bytes cannot trip a cap that 7-8 KB trips.
 * If `recoveryMode: 'server_compact'` ever coincides with repeat 431s, that
 * assumption is wrong and the budget needs revisiting.
 *
 * The proper fix is to stop building 7-8 KB URLs at all (the `clickout_url`
 * de-duplication work). This is the safety net until then.
 */
export const buildCompactRedirectLink = (redirectLink: URL): string | null => {
  const source = new URL(redirectLink.toString())
  const compact = new URL(source.origin + source.pathname)

  let carriedResolutionParam = false
  for (const param of REDIRECT_PRIMARY_RESOLUTION_PARAMS) {
    const value = source.searchParams.get(param)
    if (value) {
      compact.searchParams.set(param, value)
      carriedResolutionParam = true
      // Either/or — the token decodes to the link, so stop at the first hit.
      break
    }
  }

  // Companions are additive and unconditional. Deliberately OUTSIDE the loop
  // above (no `break`) and outside the budget below: correctness beats size,
  // and for a Google-Shopping product this param IS the resolution.
  for (const param of REDIRECT_COMPANION_RESOLUTION_PARAMS) {
    const value = source.searchParams.get(param)
    if (value) compact.searchParams.set(param, value)
  }

  // The token flow carries `link=''` as a placeholder, so an empty value is not
  // a resolution param — hence the truthiness checks above. Without a primary
  // the server answers "Link is required", which is worse than the invalid-link
  // UI the caller falls back to on null.
  if (!carriedResolutionParam) return null

  // `rh=1` before the attribution loop so the budget can never squeeze out the
  // one param that makes this a redirect rather than a text/plain body.
  compact.searchParams.set('rh', '1')

  for (const param of REDIRECT_ATTRIBUTION_PARAMS) {
    const value = source.searchParams.get(param)
    if (!value) continue
    // Try it, and back it out if it would breach the budget. Continue rather
    // than break: later params are shorter, so one long `q` should not cost us
    // the utm fields behind it.
    compact.searchParams.set(param, value)
    if (compact.toString().length > COMPACT_REDIRECT_MAX_LENGTH) {
      compact.searchParams.delete(param)
    }
  }

  return compact.toString()
}

/** How the interstitial recovered a failed resolver fetch. Reported as
 *  `recoveryMode` so each route is countable rather than all of them hiding
 *  inside `recovered: true`. */
export type ResolverRecoveryMode =
  | 'server'
  | 'server_compact'
  | 'direct'
  | 'none'

/**
 * Decide where to send the tab when the resolver fetch fails.
 *
 * One function, because the choice depends on the HTTP status and getting it
 * wrong is invisible: the tab navigates away and nothing reports whether the
 * shopper arrived. Keeping it pure means the whole decision table is
 * unit-testable, and the caller reads `recovered` off the result rather than
 * asserting it separately.
 *
 * | Cause | Route | Shopper arrives | We earn |
 * | -- | -- | -- | -- |
 * | timeout / network | `server` — same url + `rh=1` | yes | yes |
 * | `431` oversized | `server_compact` — de-nested url | yes | yes |
 * | `5xx` with a usable `?link=` | `direct` — straight to the store | yes | **no** |
 * | `5xx` without one | `server` — nothing better available | maybe | maybe |
 *
 * ⚠️ The `5xx` → `direct` case is deliberately the ONE place we give up the
 * affiliate wrap. A 5xx means the resolver itself is unhealthy, and `rh=1` asks
 * that same server again — so gambling the shopper's arrival on it is the
 * weaker bet when we already hold a real destination. It also matches what
 * shipped before this change, which makes this function no worse than current
 * behaviour in every row of that table. An error page earns nothing either.
 */
export const pickResolverRecovery = (input: {
  redirectLink: URL | null
  link: string | null
  status: number | null
}): { url: string | null; mode: ResolverRecoveryMode } => {
  const { redirectLink, link, status } = input

  // `isValidUrl` normalises (it accepts scheme-less input by prepending
  // https://) and hands back a URL, so use what it returns rather than
  // re-parsing `link` — re-parsing throws on values it just approved.
  const direct = link ? isValidUrl(link) : false
  const directUrl = direct instanceof URL ? direct.toString() : null

  if (!redirectLink) {
    // No resolver url to retry. A real `?link=` is still better than the
    // invalid-link UI.
    return directUrl
      ? { url: directUrl, mode: 'direct' }
      : { url: null, mode: 'none' }
  }

  if (status === 431) {
    const compact = buildCompactRedirectLink(redirectLink)
    if (compact) return { url: compact, mode: 'server_compact' }
    // Nothing to rebuild from — fall back rather than re-sending the oversized
    // url, which would re-block on size.
    return directUrl
      ? { url: directUrl, mode: 'direct' }
      : { url: null, mode: 'none' }
  }

  if (status !== null && status >= 500 && directUrl) {
    return { url: directUrl, mode: 'direct' }
  }

  return { url: withServerRedirectHeader(redirectLink), mode: 'server' }
}

export const getGoogleProductUrl = async (
  productId: string,
  source: string,
  googleSource: string,
  productToken: string | undefined,
  selectedCountry = 'us',
  googleUule?: string | null
): Promise<string | null> => {
  try {
    const url = new URL(`${DUPE_API_URL}/api/google-product`)
    if (productId) url.searchParams.set('productId', productId)
    if (source) url.searchParams.set('sourceShop', source)
    if (googleSource) url.searchParams.set('googleSource', googleSource)
    if (selectedCountry) url.searchParams.set('country', selectedCountry)
    if (productToken) url.searchParams.set('productToken', productToken)
    if (googleUule) url.searchParams.set('googleUule', googleUule)

    const resp = await axios.get(url.toString())
    const product = resp.data?.product || {}

    return product.link || null
  } catch (_err) {
    return null
  }
}
